import { fromZodError } from 'zod-validation-error';
import { type ZodError, z } from 'zod';
import type { BugsWrapper } from '../bugs/Bugs';
import { Query, type BlockQuery } from '.';

export type FailedBlock = {
	_modelApiKey: 'failed';
	targetModelApiKey: string;
	fromCMS: unknown;
	failedMessage: string;
};

type ValidationType<T extends ReadonlyArray<BlockQuery<unknown>>> =
	T extends ReadonlyArray<BlockQuery<infer QueryValidationType>>
		? QueryValidationType | FailedBlock
		: never;

export class BlockQueryCollection<T extends ReadonlyArray<BlockQuery<unknown>>> extends Query<
	ValidationType<T>[]
> {
	private _single: z.ZodType<ValidationType<T>>;
	public get single(): z.ZodType<ValidationType<T>> {
		return this._single;
	}

	constructor(queries: T, bugReporter?: BugsWrapper) {
		const query = queries.map((q) => q.query).join('\n');

		const itemValidator = async (block: any) => {
			// Skip failed blocks
			if (block._modelApiKey === 'failed') return block;
			if (!block._modelApiKey) {
				return {
					_modelApiKey: 'failed',
					targetModelApiKey: 'unknown',
					fromCMS: undefined,
					failedMessage: `<b>This block is missing implementation.</b><br />
					This could be due to an implementation on the CMS side, that is not yet implemented on the frontend. <br />
					Maybe your colleague is currently workin this, on a different branch?`
				};
			}

			// Validate each block individually
			const modelApiKey = block._modelApiKey;
			const validation = queries.find((v) => v.modelKey === modelApiKey)?.validation;
			if (validation) {
				const test = await validation.safeParseAsync(block);
				if (test.success) {
					return test.data;
				} else {
					const result = {
						_modelApiKey: 'failed',
						targetModelApiKey: block._modelApiKey,
						fromCMS: block,
						failedMessage: fromZodError(test.error as ZodError).toString()
					};

					console.error(result.failedMessage);
					bugReporter?.reportError(new Error(result.failedMessage), { cmsRespone: result });

					return result;
				}
			} else {
				const result = {
					_modelApiKey: 'failed',
					targetModelApiKey: block._modelApiKey,
					fromCMS: block,
					failedMessage: `No validation found for block for _modelApiKey: "${block._modelApiKey}"
					Please add a validation for this block in src/lib/BlockQueries.ts`
				};

				console.error(result.failedMessage);
				bugReporter?.reportError(new Error(result.failedMessage), { cmsRespone: result });

				return result;
			}
		};

		/**
		 * NOTE:
		 * Instead of validating the whole array, we validate each item individually.
		 * This allows us to replace invalid items with a failed block,
		 * instead of failing the entire query.
		 */
		const validation = z.array(z.any()).transform(async (blocks) => {
			const transformed = await Promise.all(blocks.map(itemValidator));

			return transformed;
		}) as unknown as z.ZodType<ValidationType<T>[]>;

		super(query, validation);

		this._single = z.any().transform(itemValidator);
	}
}
